/**
 * Insights panel — AI-powered matter intelligence and admin task management.
 *
 * Three sections:
 * 1. Matter Intelligence — key dates, matter age, reserve total, phase context
 * 2. AI Assistant — quick-action buttons (Analyse / Summarise / Check) + free-form question
 * 3. Admin Tasks — list of tasks + quick create form
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Input,
  Textarea,
  Dropdown,
  Option,
  Spinner,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Add20Regular } from '@fluentui/react-icons/svg/add';
import { CheckmarkCircle20Regular } from '@fluentui/react-icons/svg/checkmark-circle';
import { ErrorCircle20Regular } from '@fluentui/react-icons/svg/error-circle';
import { Info20Regular } from '@fluentui/react-icons/svg/info';
import { Lightbulb20Regular } from '@fluentui/react-icons/svg/lightbulb';
import { Send20Regular } from '@fluentui/react-icons/svg/send';
import { ShieldError20Regular } from '@fluentui/react-icons/svg/shield-error';
import { Sparkle20Regular } from '@fluentui/react-icons/svg/sparkle';
import { TaskListSquareLtr20Regular } from '@fluentui/react-icons/svg/task-list-square-ltr';
import { Wand20Regular } from '@fluentui/react-icons/svg/wand';
import { Warning20Regular } from '@fluentui/react-icons/svg/warning';
import { useGateway } from '../../api/useGateway';
import type { MatterInsight, InsightsResult, AdminTask, MatterSummary, ReserveData } from '../../api/gateway-client';

interface InsightsPanelProps {
  matterId: string | null;
}

const BRAND_COLOR = '#075156';

const TASK_TYPES = [
  { value: 'request-records', label: 'Request Records' },
  { value: 'book-conference', label: 'Book Conference' },
  { value: 'schedule-mediation', label: 'Schedule Mediation' },
  { value: 'obtain-report', label: 'Obtain Expert Report' },
  { value: 'file-documents', label: 'File Court Documents' },
  { value: 'update-specials', label: 'Update Specials Schedule' },
  { value: 'other', label: 'Other' },
];

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },

  // ── No matter state
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '48px 24px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  emptyIcon: {
    fontSize: '32px',
    opacity: 0.4,
  },
  emptyTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  emptyHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    maxWidth: '220px',
  },

  // ── Section structure
  section: {
    padding: '12px 12px 0',
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: tokens.colorNeutralForeground3,
    marginBottom: '8px',
    display: 'block',
  },
  divider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    margin: '12px 0 0',
  },

  // ── Matter intelligence grid
  intelGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '10px',
  },
  intelCell: {
    padding: '8px 10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    '&:nth-child(even)': {
      borderRight: 'none',
    },
    '&:nth-last-child(-n+2)': {
      borderBottom: 'none',
    },
  },
  intelLabel: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: tokens.colorNeutralForeground3,
  },
  intelValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  intelValueMuted: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
  intelValueHighlight: {
    fontSize: '13px',
    fontWeight: 700,
    color: BRAND_COLOR,
  },
  intelLoading: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  // ── AI assistant
  quickActions: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '10px',
  },
  quickBtn: {
    fontSize: '12px',
    height: '28px',
    borderRadius: '14px',
    border: `1px solid ${BRAND_COLOR}`,
    color: BRAND_COLOR,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: '0 10px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: `${BRAND_COLOR}10`,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  aiLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 0',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  insightCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '10px',
  },
  insightCard: {
    padding: '10px 12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  insightCardInfo: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  insightCardWarning: {
    backgroundColor: '#fffbf0',
    border: `1px solid #f0c040`,
  },
  insightCardRisk: {
    backgroundColor: '#fff5f5',
    border: `1px solid #f0a0a0`,
  },
  insightCardSuggestion: {
    backgroundColor: '#f0f9ff',
    border: `1px solid #90c8e8`,
  },
  insightCardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  insightCardTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    flex: 1,
  },
  insightCardBody: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.5',
  },
  insightCardAction: {
    fontSize: '11px',
    color: BRAND_COLOR,
    fontWeight: 600,
    marginTop: '2px',
    cursor: 'default',
  },
  modelBadge: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground4,
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  questionRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'flex-end',
    marginTop: '8px',
  },
  questionInput: {
    flex: 1,
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 10px',
    borderRadius: '6px',
    backgroundColor: '#fff5f5',
    border: `1px solid #f0a0a0`,
    fontSize: '12px',
    color: '#b91c1c',
    marginBottom: '8px',
  },
  noInsights: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    padding: '4px 0 8px',
  },

  // ── Admin tasks
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '10px',
  },
  taskItem: {
    padding: '8px 10px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  taskItemCompleted: {
    opacity: 0.55,
  },
  taskIcon: {
    marginTop: '1px',
    flexShrink: 0,
  },
  taskContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  taskTitle: {
    fontSize: '12px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
  },
  taskMeta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  taskComplete: {
    flexShrink: 0,
    fontSize: '11px',
    height: '22px',
    minWidth: 'auto',
    padding: '0 8px',
  },
  noTasks: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    padding: '4px 0',
  },
  createTaskForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: '6px',
  },
  createTaskFormTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  formRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'flex-end',
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function matterAge(incidentDate: string): string {
  const ms = Date.now() - new Date(incidentDate).getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days < 30) return `${days} days`;
  if (days < 365) return `${Math.floor(days / 30)} months`;
  const years = days / 365.25;
  return years < 2
    ? `${Math.floor(years)} year ${Math.floor((years % 1) * 12)} months`
    : `${years.toFixed(1)} years`;
}

function InsightIcon({ type }: { type: MatterInsight['type'] }) {
  if (type === 'warning') return <Warning20Regular style={{ color: '#d97706', fontSize: 16, flexShrink: 0 }} />;
  if (type === 'risk') return <ShieldError20Regular style={{ color: '#dc2626', fontSize: 16, flexShrink: 0 }} />;
  if (type === 'suggestion') return <Lightbulb20Regular style={{ color: '#0284c7', fontSize: 16, flexShrink: 0 }} />;
  return <Info20Regular style={{ color: BRAND_COLOR, fontSize: 16, flexShrink: 0 }} />;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InsightsPanel: React.FC<InsightsPanelProps> = ({ matterId }) => {
  const styles = useStyles();
  const gateway = useGateway();

  const [matter, setMatter] = useState<MatterSummary | null>(null);
  const [matterLoading, setMatterLoading] = useState(false);
  const [reserves, setReserves] = useState<ReserveData | null>(null);
  const [reservesLoading, setReservesLoading] = useState(false);
  const [reservesFailed, setReservesFailed] = useState(false);
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [insightsResult, setInsightsResult] = useState<InsightsResult | null>(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [question, setQuestion] = useState('');
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTaskType, setNewTaskType] = useState('request-records');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [newTaskDue, setNewTaskDue] = useState('');

  // Track the last matterId we auto-loaded so we don't double-fire
  const autoLoadedFor = useRef<string | null>(null);

  const fetchInsights = useCallback(async (q?: string) => {
    if (!gateway || !matterId) return;
    setIsLoadingInsights(true);
    setInsightsError(null);
    try {
      const result = await gateway.getMatterInsights(matterId, q ? { question: q } : {});
      setInsightsResult(result);
    } catch (err) {
      setInsightsError('Could not load insights. Check your connection and try again.');
      console.error('[InsightsPanel] fetchInsights error:', err);
    } finally {
      setIsLoadingInsights(false);
    }
  }, [gateway, matterId]);

  // Load matter + reserves + auto-load insights when panel becomes active
  useEffect(() => {
    if (!gateway || !matterId) {
      setMatter(null);
      setReserves(null);
      setReservesFailed(false);
      setInsightsResult(null);
      setInsightsError(null);
      setTasks([]);
      autoLoadedFor.current = null;
      return;
    }

    setMatterLoading(true);
    gateway.getMatter(matterId)
      .then(setMatter)
      .catch(() => {})
      .finally(() => setMatterLoading(false));

    setReservesLoading(true);
    setReservesFailed(false);
    gateway.getReserves(matterId)
      .then(setReserves)
      .catch(() => setReservesFailed(true))
      .finally(() => setReservesLoading(false));

    setIsLoadingTasks(true);
    gateway.getAdminTasks(matterId)
      .then(setTasks)
      .catch(() => {})
      .finally(() => setIsLoadingTasks(false));

    // Auto-load rule-based insights on first view of each matter
    if (autoLoadedFor.current !== matterId) {
      autoLoadedFor.current = matterId;
      void fetchInsights();
    }
  }, [gateway, matterId, fetchInsights]);

  const handleQuickAction = (prompt: string) => {
    void fetchInsights(prompt);
  };

  const handleAsk = () => {
    const q = question.trim();
    if (!q) return;
    setQuestion('');
    void fetchInsights(q);
  };

  const handleCreateTask = async () => {
    if (!gateway || !matterId || !newTaskTitle.trim()) return;
    setIsCreatingTask(true);
    try {
      const task = await gateway.createAdminTask(matterId, {
        typeSystemName: newTaskType,
        title: newTaskTitle.trim(),
        description: newTaskDesc.trim() || undefined,
        dueDate: newTaskDue || undefined,
      });
      setTasks((prev) => [task, ...prev]);
      setNewTaskTitle('');
      setNewTaskDesc('');
      setNewTaskDue('');
      setShowCreateForm(false);
    } catch {
      // Silently fail for now — task creation errors are rare
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    if (!gateway || !matterId) return;
    try {
      const updated = await gateway.completeAdminTask(matterId, taskId);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    } catch {
      // Silently fail
    }
  };

  // ── No matter linked
  if (!matterId) {
    return (
      <div className={styles.empty}>
        <Sparkle20Regular className={styles.emptyIcon} />
        <span className={styles.emptyTitle}>No matter linked</span>
        <span className={styles.emptyHint}>Link a matter in the Matter tab to see AI insights and manage tasks.</span>
      </div>
    );
  }

  const incidentDateStr = matter?.incidentDate ? formatDate(matter.incidentDate) : null;
  const caseAge = matter?.incidentDate ? matterAge(matter.incidentDate) : null;

  const grandTotal = reserves?.grandTotal ?? null;
  const reservesDisplay = reservesFailed
    ? null
    : reservesLoading
      ? undefined   // undefined = "loading"
      : grandTotal;

  const insightCardStyle = (type: MatterInsight['type']): string => {
    if (type === 'warning') return `${styles.insightCard} ${styles.insightCardWarning}`;
    if (type === 'risk') return `${styles.insightCard} ${styles.insightCardRisk}`;
    if (type === 'suggestion') return `${styles.insightCard} ${styles.insightCardSuggestion}`;
    return `${styles.insightCard} ${styles.insightCardInfo}`;
  };

  const pendingTasks = tasks.filter((t) => t.status === 'pending');
  const completedTasks = tasks.filter((t) => t.status === 'completed');

  return (
    <div className={styles.container}>

      {/* ── MATTER INTELLIGENCE ───────────────────────────────── */}
      <div className={styles.section}>
        <span className={styles.sectionLabel}>Matter Intelligence</span>
        <div className={styles.intelGrid}>
          <div className={styles.intelCell}>
            <span className={styles.intelLabel}>Incident Date</span>
            {matterLoading
              ? <span className={styles.intelLoading}><Spinner size="extra-tiny" />Loading…</span>
              : incidentDateStr
                ? <span className={styles.intelValue}>{incidentDateStr}</span>
                : <span className={styles.intelValueMuted}>Not recorded</span>}
          </div>
          <div className={styles.intelCell}>
            <span className={styles.intelLabel}>Claim Reference</span>
            {matterLoading
              ? <span className={styles.intelLoading}><Spinner size="extra-tiny" />Loading…</span>
              : matter?.claimReference
                ? <span className={styles.intelValue} style={{ fontFamily: 'monospace', fontSize: '11px' }}>{matter.claimReference}</span>
                : <span className={styles.intelValueMuted}>Not recorded</span>}
          </div>
          <div className={styles.intelCell}>
            <span className={styles.intelLabel}>Matter Age</span>
            {matterLoading
              ? <span className={styles.intelLoading}><Spinner size="extra-tiny" />Loading…</span>
              : caseAge
                ? <span className={styles.intelValue}>{caseAge}</span>
                : <span className={styles.intelValueMuted}>—</span>}
          </div>
          <div className={styles.intelCell}>
            <span className={styles.intelLabel}>Current Reserves</span>
            {reservesDisplay === undefined
              ? <span className={styles.intelLoading}><Spinner size="extra-tiny" />Loading…</span>
              : reservesDisplay === null
                ? <span className={styles.intelValueMuted}>Not available</span>
                : (
                  <span className={styles.intelValueHighlight}>
                    ${reservesDisplay.toLocaleString('en-AU')}
                  </span>
                )}
          </div>
        </div>
      </div>

      <div className={styles.divider} />

      {/* ── AI ASSISTANT ─────────────────────────────────────── */}
      <div className={styles.section}>
        <span className={styles.sectionLabel}>AI Assistant</span>

        <div className={styles.quickActions}>
          <button
            className={styles.quickBtn}
            disabled={isLoadingInsights}
            onClick={() => handleQuickAction('Analyse this matter and surface key risks and issues at the current phase.')}
          >
            <ShieldError20Regular style={{ fontSize: 14 }} />
            Analyse risks
          </button>
          <button
            className={styles.quickBtn}
            disabled={isLoadingInsights}
            onClick={() => handleQuickAction('What should this advice letter address at the current phase of proceedings?')}
          >
            <Wand20Regular style={{ fontSize: 14 }} />
            What to address
          </button>
          <button
            className={styles.quickBtn}
            disabled={isLoadingInsights}
            onClick={() => handleQuickAction('Are the current reserves appropriate given the phase and heads of damage? What reserve categories need attention?')}
          >
            <Lightbulb20Regular style={{ fontSize: 14 }} />
            Check reserves
          </button>
        </div>

        {isLoadingInsights && (
          <div className={styles.aiLoading}>
            <Spinner size="tiny" />
            Generating insights…
          </div>
        )}

        {insightsError && !isLoadingInsights && (
          <div className={styles.errorBanner}>
            <ErrorCircle20Regular style={{ fontSize: 16, flexShrink: 0 }} />
            {insightsError}
          </div>
        )}

        {insightsResult && !isLoadingInsights && !insightsError && (
          <>
            <div className={styles.modelBadge}>
              <Sparkle20Regular style={{ fontSize: 12 }} />
              {insightsResult.model === 'ai' ? 'AI-generated' : 'Rule-based'} · {new Date(insightsResult.generatedAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
            </div>
            {insightsResult.insights.length === 0 ? (
              <span className={styles.noInsights}>No specific insights at this stage.</span>
            ) : (
              <div className={styles.insightCards}>
                {insightsResult.insights.map((insight, i) => (
                  <div key={i} className={insightCardStyle(insight.type)}>
                    <div className={styles.insightCardTop}>
                      <InsightIcon type={insight.type} />
                      <span className={styles.insightCardTitle}>{insight.title}</span>
                      <Badge
                        appearance="ghost"
                        size="small"
                        color={
                          insight.type === 'risk' ? 'danger'
                            : insight.type === 'warning' ? 'warning'
                              : insight.type === 'suggestion' ? 'informative'
                                : 'subtle'
                        }
                      >
                        {insight.type}
                      </Badge>
                    </div>
                    <span className={styles.insightCardBody}>{insight.body}</span>
                    {insight.action && (
                      <span className={styles.insightCardAction}>→ {insight.action}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Free-form question */}
        <div className={styles.questionRow}>
          <Input
            className={styles.questionInput}
            placeholder="Ask a question about this matter…"
            value={question}
            onChange={(_e, d) => setQuestion(d.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAsk();
              }
            }}
            size="small"
          />
          <Button
            appearance="primary"
            size="small"
            icon={<Send20Regular />}
            disabled={!question.trim() || isLoadingInsights}
            onClick={handleAsk}
            style={{ backgroundColor: BRAND_COLOR, minWidth: 'auto' }}
            title="Send question"
          />
        </div>
      </div>

      <div className={styles.divider} />

      {/* ── ADMIN TASKS ──────────────────────────────────────── */}
      <div className={styles.section} style={{ paddingBottom: '12px' }}>
        <div className={styles.sectionHeaderRow}>
          <span className={styles.sectionLabel} style={{ marginBottom: 0 }}>Admin Tasks</span>
          <Button
            appearance="subtle"
            size="small"
            icon={<Add20Regular />}
            onClick={() => setShowCreateForm((v) => !v)}
            style={{ color: BRAND_COLOR, fontSize: '12px', height: '24px' }}
          >
            New task
          </Button>
        </div>

        {showCreateForm && (
          <div className={styles.createTaskForm}>
            <span className={styles.createTaskFormTitle}>Create task for admin</span>
            <Dropdown
              size="small"
              value={TASK_TYPES.find((t) => t.value === newTaskType)?.label ?? 'Request Records'}
              onOptionSelect={(_e, d) => setNewTaskType(d.optionValue ?? 'request-records')}
              placeholder="Task type"
            >
              {TASK_TYPES.map((t) => (
                <Option key={t.value} value={t.value}>{t.label}</Option>
              ))}
            </Dropdown>
            <Input
              size="small"
              placeholder="Task title / instruction…"
              value={newTaskTitle}
              onChange={(_e, d) => setNewTaskTitle(d.value)}
            />
            <Textarea
              size="small"
              placeholder="Additional notes (optional)"
              value={newTaskDesc}
              onChange={(_e, d) => setNewTaskDesc(d.value)}
              rows={2}
            />
            <div className={styles.formRow}>
              <Input
                size="small"
                type="date"
                value={newTaskDue}
                onChange={(_e, d) => setNewTaskDue(d.value)}
                style={{ flex: 1 }}
                title="Due date (optional)"
              />
              <Button
                appearance="primary"
                size="small"
                onClick={() => void handleCreateTask()}
                disabled={!newTaskTitle.trim() || isCreatingTask}
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {isCreatingTask ? <Spinner size="tiny" /> : 'Create'}
              </Button>
            </div>
          </div>
        )}

        {isLoadingTasks ? (
          <div className={styles.aiLoading}>
            <Spinner size="tiny" />
            Loading tasks…
          </div>
        ) : (
          <div className={styles.taskList}>
            {pendingTasks.length === 0 && completedTasks.length === 0 && (
              <span className={styles.noTasks}>No admin tasks yet. Create one to assign work to your team.</span>
            )}
            {pendingTasks.map((task) => (
              <div key={task.id} className={styles.taskItem}>
                <TaskListSquareLtr20Regular className={styles.taskIcon} style={{ color: BRAND_COLOR, fontSize: 18 }} />
                <div className={styles.taskContent}>
                  <span className={styles.taskTitle}>{task.title}</span>
                  <span className={styles.taskMeta}>
                    {task.typeLabel}
                    {task.dueDate ? ` · Due ${formatDate(task.dueDate)}` : ''}
                    {task.assignedToName ? ` · ${task.assignedToName}` : ''}
                  </span>
                </div>
                <Button
                  appearance="outline"
                  size="small"
                  className={styles.taskComplete}
                  onClick={() => void handleCompleteTask(task.id)}
                  title="Mark as complete"
                >
                  Done
                </Button>
              </div>
            ))}
            {completedTasks.map((task) => (
              <div key={task.id} className={`${styles.taskItem} ${styles.taskItemCompleted}`}>
                <CheckmarkCircle20Regular className={styles.taskIcon} style={{ color: tokens.colorPaletteGreenForeground1, fontSize: 18 }} />
                <div className={styles.taskContent}>
                  <span className={styles.taskTitle}>{task.title}</span>
                  <span className={styles.taskMeta}>
                    {task.typeLabel}
                    {task.completedAt ? ` · Done ${formatDate(task.completedAt)}` : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
